import { convertMbpsToGbps } from '@giffgaff/broadband-common'

import { Plan, PlanAmendmentDetails } from '@/api/api.types'

import { formatDate, shortMonthInformal } from '../formatDate/formatDate'

type CannotChangeReason = {
  title?: string
  copy: string
  variant?: 'error' | 'warning'
  href?: string
  linkText?: string
}

const cannotChangeReasonsByCode = {
  ['plan.amendment.too_close_to_plan_start']: {
    title: 'You can only change your plan once a month',
    copy: 'Your plan’s recently been sorted. You’ll be able to change it again from {{canChangeFromDate}}.',
  },
  ['plan.amendment.too_close_to_next_bill']: {
    title: 'You can’t change your plan right now',
    copy: `As your plan is due to refresh in the next couple of hours, we can’t make changes at this point.`,
  },
  ['plan.amendment.too_far_in_future']: {
    title: 'You can’t change your plan right now',
    copy: `As your plan refreshed recently, we can’t make changes at this point. Please check back on {{canChangeFromDate}} to make changes.`,
  },
  ['plan.amendment.upgrade_in_progress']: {
    title: 'We’re upgrading your speed',
    copy: 'Sit tight. Your {{name}} {{speed}} plan will kick in within the next couple of hours.',
  },
  ['plan.amendment.downgrade_in_progress']: {
    title: 'We’re updating your plan',
    copy: 'Sit tight. Your {{speed}} plan will kick in {{startDateWithPreposition}}. Check back here then to change it again.',
  },
  ['plan.amendment.plan_is_inactive']: {
    title: 'Your plan is inactive',
    copy: 'You can’t change your plan right now.',
  },
  // 'plan.amendment.plan_is_ceased' entry removed: falls through to the `unknown` copy instead.
  // See https://giffgaff.atlassian.net/browse/BBND-10218
  // ['plan.amendment.plan_is_ceased']: {
  //   title: 'Your order cancellation is being processed',
  //   copy: "We'll let you know the outcome",
  // },
  ['plan.amendment.plan_is_paused']: {
    title: 'You can’t change your plan as it’s currently paused.',
    copy: 'You’ll need to unpause your plan first, then try again.',
  },
  ['plan.in_arrears']: {
    title: "You've missed a payment, so you can't upgrade your plan right now.",
    copy: 'Update your card details to make the payment now, then try to change plan again.',
    href: '/profile/payment-details',
    linkText: 'Review and pay',
  },
  ['plan.upgrade_disabled']: {
    copy: "Unfortunately we're unable to support plan upgrades right now. Please check back later.",
    variant: 'warning',
  },
  unknown: { title: 'You can’t change your plan right now', copy: '' },
} as const

export const cannotChangeReasons: Record<keyof typeof cannotChangeReasonsByCode, CannotChangeReason> =
  cannotChangeReasonsByCode

export function formatCannotChangeInfo(
  newPlan?: Pick<Plan, 'displayName' | 'downstreamSpeedInMbps' | 'provisionalStartDate' | 'startDateTime'>,
  amendmentDetails?: Pick<PlanAmendmentDetails, 'canAmendFrom' | 'reason'>,
) {
  const canChangeFromDate = formatDate(amendmentDetails?.canAmendFrom, {
    options: shortMonthInformal,
    defaultValue: 'next month',
  })

  return newPlan
    ? {
        name: newPlan.displayName,
        speed: convertMbpsToGbps(newPlan.downstreamSpeedInMbps),
        canChangeFromDate,
        startDateWithPreposition: newPlan.provisionalStartDate
          ? `on ${formatDate(newPlan.provisionalStartDate, { options: shortMonthInformal })}`
          : 'next month',
      }
    : {
        name: 'new',
        speed: 'amended', // fallback copy approved by copy team; not ideal but it should usually be an edge case
        canChangeFromDate,
        startDateWithPreposition: 'next month',
      }
}

export function getNotificationCopy(reason?: NonNullable<PlanAmendmentDetails['reason']>) {
  return reason && reason in cannotChangeReasons
    ? cannotChangeReasons[reason as keyof typeof cannotChangeReasons]
    : cannotChangeReasons.unknown
}
